import React from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { companyInfo } from '../data/mock';
import { SEO } from '../components/SEO';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Privacy Policy"
        path="/privacy"
        description="Naviton Global's privacy policy detailing how we collect, use, and protect your personal information."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-12 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg text-slate-300">
                Last updated: January 2026
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-slate max-w-none">
              
              <h2 className="text-2xl font-bold text-navy-900 mb-4">1. Introduction</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {companyInfo.name} ("we", "our", or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website or use our services.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">2. Information We Collect</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                We may collect information about you in a variety of ways, including:
              </p>
              <h3 className="text-xl font-semibold text-navy-900 mb-3">Personal Data</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Name and contact information (email address, phone number)</li>
                <li>Company name and business details</li>
                <li>Country of residence or business operation</li>
                <li>Enquiry details and correspondence</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold text-navy-900 mb-3">Automatically Collected Data</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">3. How We Use Your Information</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Respond to your enquiries and provide customer service</li>
                <li>Process and manage trade-related requests</li>
                <li>Send you relevant information about our services</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraudulent or illegal activity</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">4. Disclosure of Your Information</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                We may share your information in the following situations:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li><strong>Business Partners:</strong> With trusted partners who assist us in providing services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition</li>
                <li><strong>With Your Consent:</strong> For any other purpose with your permission</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">5. Data Security</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal 
                information against unauthorised access, alteration, disclosure, or destruction. However, 
                no method of transmission over the Internet is 100% secure, and we cannot guarantee 
                absolute security.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">6. Your Rights</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                Under Australian Privacy Law, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">7. Cookies and Tracking</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your experience on our website. 
                These technologies help us understand how you use our site, remember your preferences, 
                and provide relevant content. You can control cookie settings through your browser.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">8. FuelIQ Application</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                FuelIQ is a digital venture operated by {companyInfo.name}. It is a smart fuel-price
                comparison and savings platform for Australian drivers. This section explains how we
                handle information collected through the FuelIQ application.
              </p>
              <h3 className="text-xl font-semibold text-navy-900 mb-3">Information Collected via FuelIQ</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li><strong>Account information:</strong> When you sign in with Google, we receive your
                  name, email address and profile picture as authorised by you through Google Sign-In.</li>
                <li><strong>Location data:</strong> With your permission, we use your device location to
                  show nearby fuel stations and relevant prices. You can disable location access at any
                  time in your device settings.</li>
                <li><strong>Usage data:</strong> Searches, saved stations and preferences used to
                  personalise your experience and calculate savings.</li>
              </ul>
              <h3 className="text-xl font-semibold text-navy-900 mb-3">How FuelIQ Uses This Information</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Authenticate you and maintain your account</li>
                <li>Display live and nearby fuel prices relevant to your location</li>
                <li>Calculate and present potential savings</li>
                <li>Improve the accuracy and performance of the application</li>
              </ul>
              <p className="text-slate-600 mb-6 leading-relaxed">
                FuelIQ's use of information received from Google APIs adheres to the Google API Services
                User Data Policy, including the Limited Use requirements. We do not sell your personal
                information, and we only retain it for as long as necessary to provide the service or as
                required by law. You may request deletion of your FuelIQ data at any time by contacting us
                using the details below.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">9. Third-Party Links</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the 
                privacy practices or content of these external sites. We encourage you to review their 
                privacy policies before providing any personal information.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">10. Changes to This Policy</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new Privacy Policy on this page and updating the "Last updated" date. 
                Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">11. Contact Us</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <p className="text-slate-700 font-semibold">{companyInfo.name}</p>
                <p className="text-slate-600">Email: info@navitonglobal.com.au</p>
                <p className="text-slate-600">Phone: +61 478822581</p>
                <p className="text-slate-600">Address: Sydney, Australia</p>
                <p className="text-slate-600">ABN: {companyInfo.abn}</p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
